<main id="main-content" class="h-screen flex flex-col pl-8 py-4 pr-4 transition-all duration-0">
    <div class="w-full flex flex-col p-2 items-center gap-2">
        <div class="w-full flex flex-row items-center justify-between">
            <div class="w-full flex gap-2 items-center justify-start">
                <h1 class="oswald-font text-3xl uppercase">
                    Courses of Action for Functions
                </h1>
                <div data-popover-target="popover-bottom" data-popover-placement="bottom" type="button">
                    <lucide-icon [name]="icons.CircleHelp" [size]=24 class="text-red-500"></lucide-icon>
                </div>
                <div data-popover id="popover-bottom" role="tooltip" class="absolute z-10 invisible inline-block max-w-96 text-sm text-gray-500 transition-opacity duration-300 bg-white border border-gray-200 rounded-md shadow-xs opacity-0">
                    <div class="px-3 py-2">
                        <p>
                            To develop <strong>Courses of Action</strong> for each function, click on the <strong>Function</strong> in the <strong>Functions</strong> selection. When selected, it will be highlighted. In the <strong>Courses of Action</strong> section, fill out the <strong>Courses of Action</strong> for before, during, and after. When completed, click on 
                            <span class="inline-flex px-3.5 py-1 flex items-center justify-center text-white oswald-font bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 rounded-md">
                                Save
                            </span>. This must be completed for each <strong>Function</strong>. 
                        </p>
                    </div>
                    <div data-popper-arrow></div>
                </div>
            </div>
            <eop-assistant
                [sectionId]="sectionTypeId"
            ></eop-assistant> 
        </div>
    </div>
    
    <!-- MAIN CONTENT -->
    <div class="p-2 w-full flex flex-grow flex-row items-start gap-8 overflow-y-auto">
        <div class="w-1/3 max-h-full bg-white rounded-md shadow-custom-2 p-6 flex flex-col gap-4">
            <div class="flex flex-row w-full items-center justify-between">
                <h2 class="oswald-font text-3xl font-medium">FUNCTIONS</h2>
            </div>
            <div class="h-full w-full flex flex-wrap text-gray-900 overflow-y-auto">
                <div class="h-full w-full mr-2 flex flex-col gap-2">
                    <ng-container *ngIf="functionsList.length > 0; else emptyFunctionsList">
                        <div 
                            *ngFor="let function of functionsList"
                            class="w-full flex flex-row items-stretch gap-2"
                        >
                            <button
                                class="min-h-10 flex-1 px-2.5 py-1.5 flex flex-row gap-2 items-center justify-between rounded-md hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200 hover:text-white hover:border-nside-orange-100 group"
                                [ngClass]="{
                                    'bg-gray-50 border border-nside-gray-300': selectedFunction !== function,
                                    'bg-gradient-to-b from-nside-orange-100 to-nside-orange-200 text-white border-nside-orange-100': selectedFunction === function
                                    }"
                                (click)="$event.preventDefault(); setFunction(function);"
                            >
                            <span
                                class="min-w-0 flex-1 text-start whitespace-pre-line break-words break-all [overflow-wrap:anywhere]"
                            >
                                    {{ function.function_name }}
                                </span>
                            </button>
                        </div>
                    </ng-container>
                    <ng-template #emptyFunctionsList>
                        <div class="text-nside-gray-100 italic">
                            Empty Function list
                        </div>
                    </ng-template>
                </div>
            </div>                
        </div>
        <div class="w-2/3 max-h-full bg-white rounded-md shadow-custom-2 p-6 flex flex-col gap-4">
            <div class="w-full flex items-start justify-start gap-2">
                <h2 class="min-w-0 w-full oswald-font text-3xl text-start">
                    Courses of Action:
                    <span
                        class="oswald-font text-3xl text-nside-orange-400 whitespace-pre-line break-words text-wrap block"
                    >
                        {{ selectedFunction.function_name }}
                    </span>
                </h2>
            </div>
            <!-- <div class="w-full h-full text-sm">
                <p>Use the following textbox to develop the courses of action. Ensure to answer the following questions: What actions nees to occur? Who is responsible for action? When should the course of action occur? How long will it take? What must occur before and after? What resources are available and what resources are needed? What is the impact on specific populations (those with disabilities, etc.)?</p>
            </div> -->
            <ng-container *ngIf="functionsList.length > 0; else emptyCoursesOfAction">
                <form 
                    (ngSubmit)="$event.preventDefault(); onSubmit();"
                    class="w-full h-full flex flex-col gap-4 overflow-y-auto"
                >
                    <div class="pr-2 pb-2 w-full flex-grow flex-col items-center gap-2 overflow-y-auto">
                        <div class="w-full flex flex-col items-start text-start gap-2">
                            <div class="w-full flex items-center oswald-font text-xl">
                                <p>Before:</p>
                            </div>
                            <div *ngIf="!canSave()" class="w-full whitespace-pre-line break-words">{{ functionCourseOfActionForm.before_description || 'Empty' }}</div>
                            <div class="pl-1 w-full flex items-center">
                                <textarea noEmoji 
                                    *ngIf="canSave()"
                                    [(ngModel)]="functionCourseOfActionForm.before_description" 
                                    name="before_description" required
                                    rows="10" 
                                    class="bg-gray-50 p-2 w-full text-sm text-gray-900 rounded-md border border-gray-300" 
                                    placeholder="Develop courses of action here..."></textarea>
                            </div>
                        </div>
                        <div class="w-full flex flex-col items-start text-start gap-2">
                            <div class="w-full flex items-center oswald-font text-xl">
                                <p>During:</p>
                            </div>
                            <div *ngIf="!canSave()" class="w-full whitespace-pre-line break-words">{{ functionCourseOfActionForm.during_description || 'Empty' }}</div>
                            <div class="pl-1 w-full flex items-center">
                                <textarea noEmoji 
                                    *ngIf="canSave()"
                                    [(ngModel)]="functionCourseOfActionForm.during_description" 
                                    name="during_description" required
                                    rows="10" 
                                    class="bg-gray-50 p-2 w-full text-sm text-gray-900 rounded-md border border-gray-300" 
                                    placeholder="Develop courses of action here..."></textarea>
                            </div>
                        </div>
                        <div class="w-full flex flex-col items-start text-start gap-2">
                            <div class="w-full flex items-center oswald-font text-xl">
                                <p>After:</p>
                            </div>
                            <div *ngIf="!canSave()" class="w-full whitespace-pre-line break-words">{{ functionCourseOfActionForm.after_description || 'Empty' }}</div>
                            <div class="pl-1 w-full flex items-center">
                                <textarea noEmoji
                                    *ngIf="canSave()"
                                    [(ngModel)]="functionCourseOfActionForm.after_description" 
                                    name="after_description" required
                                    rows="10" 
                                    class="bg-gray-50 p-2 w-full text-sm text-gray-900 rounded-md border border-gray-300" 
                                    placeholder="Develop courses of action here..."></textarea>
                            </div>
                        </div>
                    </div>
                    <div class="w-full flex flex-row gap-2 items-center justify-end">
                        <div *ngIf="latestUpdate.last_updated_at != ''" class="w-full flex gap-1 items-start justify-start">
                            <p class="oswald-font font-normal">
                                Last Updated At: <span class="raleway-font">{{  latestUpdate.last_updated_at | date: 'MM/dd/yyyy - HH:mm' }}</span>
                            </p>
                            <p class="oswald-font font-normal">
                                By: <span class="raleway-font">{{ latestUpdate.last_updated_user_name || 'None' }}</span>
                            </p>
                        </div>
                        <button
                            *ngIf="canSave()"
                            type="submit"
                            class="w-20 flex items-center justify-center px-4 py-1.5 rounded-md border bg-gradient-to-b oswald-font text-white"
                            [ngClass]="{
                                'border-nside-gray-100 from-nside-gray-100 to-nside-gray-200 cursor-not-allowed': !selectedFunction || !hasFormChanged(),
                                'border-nside-orange-100 from-nside-orange-300 to-nside-orange-400 hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200': selectedFunction && hasFormChanged()
                            }"
                            [disabled]="!selectedFunction || !hasFormChanged()"
                        >
                            Save
                        </button>
                    </div>
                </form>
            </ng-container>
            <ng-template #emptyCoursesOfAction>
                <div class="text-nside-gray-100 italic">
                    Empty Course of Action
                </div>
            </ng-template>
        </div>
    </div>
    
    <div *ngIf="loading">
        <spinner></spinner>
    </div>

    <introduction></introduction>

    <popup-message 
    [type]="messageModal"
    [message]="popupMessage"
    (close)="resetMessageModal()"
    ></popup-message>

    <prv-next-buttons [totalPages]="(eopNavigationService.totalPages$ | async) || []" />

</main>